<template>
  <div class="market-status-badge d-flex flex-wrap align-items-center gap-1 gap-md-2 ms-0 ms-md-3">
    <StrategyPill
      :label="profileBadgeLabel"
      :strategy="loading ? null : activeProfile"
      class="small"
    />
    <span :class="['badge', 'rounded-pill', 'fw-semibold', 'small', outlookBadgeClass]">
      {{ outlookBadgeLabel }}
    </span>
    <span
      v-if="!loading && predictionAvailable"
      :class="['badge', 'rounded-pill', 'fw-semibold', 'small', tradeableBadgeClass]"
    >
      {{ tradeableBadgeLabel }}
    </span>
    <StrategyPill
      v-for="ticker in INDEX_TICKERS"
      :key="ticker"
      :label="ticker"
      :strategy="perIndexStrategies[ticker] || null"
      :tooltip="perIndexTooltip(ticker)"
      class="small"
    />
  </div>
</template>

<script>
import { fetchMarketStatus } from '../services/api';
import StrategyPill from './StrategyPill.vue';
import {
  getOutlookBadgeClass,
  getOutlookIcon,
} from '../utils/marketOutlook';

const PROFILE_LABEL = {
  AGGRESSIVE:   '⚔ AGGRESSIVE',
  CONSERVATIVE: '🛡 CONSERVATIVE',
  DEFENSIVE:    '🔰 DEFENSIVE',
};

const INDEX_TICKERS = ['SPY', 'QQQ', 'DIA', 'IWM'];

export default {
  name: 'MarketStatusBadge',
  components: { StrategyPill },
  data() {
    return {
      activeProfile: null,
      marketOutlook: null,
      tradeable: false,
      predictionAvailable: false,
      perIndexStrategies: {},
      perIndexOutlooks: {},
      loading: true,
      pollInterval: null,
      prevPerIndexStrategies: null,
      INDEX_TICKERS,
    };
  },
  computed: {
    profileBadgeLabel() {
      if (this.loading) return 'Loading…';
      return PROFILE_LABEL[this.activeProfile] || `🔘 ${this.activeProfile || 'UNKNOWN'}`;
    },
    outlookBadgeClass() {
      if (this.loading) return 'bg-secondary';
      return getOutlookBadgeClass(this.marketOutlook);
    },
    outlookBadgeLabel() {
      if (this.loading) return 'Loading…';
      if (!this.marketOutlook) return '— NO DATA';
      return `${getOutlookIcon(this.marketOutlook)} ${this.marketOutlook}`;
    },
    tradeableBadgeClass() {
      return this.tradeable ? 'bg-success' : 'bg-danger';
    },
    tradeableBadgeLabel() {
      return this.tradeable ? '✅ Tradeable' : '⛔ Stand Aside';
    },
  },
  mounted() {
    this.poll();
    this.pollInterval = setInterval(this.poll, 60_000);
  },
  beforeUnmount() {
    clearInterval(this.pollInterval);
  },
  methods: {
    perIndexTooltip(ticker) {
      const strategy = this.perIndexStrategies[ticker] || 'UNKNOWN';
      const outlookInfo = this.perIndexOutlooks[ticker];
      if (!outlookInfo) {
        return `${ticker}: ${strategy} · no outlook data`;
      }
      const outlook = outlookInfo.outlook || 'UNKNOWN';
      const confidence = outlookInfo.confidence == null
        ? 'n/a'
        : `${Math.round(outlookInfo.confidence)}%`;
      return `${ticker}: ${strategy} · ${outlook} · conf ${confidence}`;
    },
    async poll() {
      try {
        const data = await fetchMarketStatus();
        this.activeProfile = data.active_profile ?? null;
        this.marketOutlook = data.market_outlook ?? null;
        this.tradeable = data.tradeable === true;
        this.predictionAvailable = data.prediction_available === true;
        this.perIndexStrategies = data.per_index_strategies ?? {};
        this.perIndexOutlooks = data.per_index_outlooks ?? {};
        this.loading = false;

        const perIndexCount = INDEX_TICKERS.filter(
          (t) => (this.perIndexStrategies[t] ?? null) !== null,
        ).length;
        if (data.per_index_strategies == null && data.per_index_outlooks == null) {
          console.warn(
            'market_status.per_index_missing: response omitted per-index data — running against pre-rollout backend?',
          );
        }
        console.info(
          `market_status.polled: profile=${this.activeProfile} outlook=${this.marketOutlook} tradeable=${this.tradeable} per_index=${perIndexCount}`,
        );

        const currentSnapshot = this.snapshotPerIndexStrategies(this.perIndexStrategies);
        if (
          this.prevPerIndexStrategies !== null
          && !this.perIndexStrategiesEqual(this.prevPerIndexStrategies, currentSnapshot)
        ) {
          console.info('market_status.per_index_strategies_changed', {
            previous: this.prevPerIndexStrategies,
            current: currentSnapshot,
            activeProfile: this.activeProfile,
          });
        }
        this.prevPerIndexStrategies = currentSnapshot;
      } catch (err) {
        console.warn('market_status.fetch_failed:', err.message);
      }
    },
    snapshotPerIndexStrategies(source) {
      const snapshot = {};
      for (const ticker of INDEX_TICKERS) {
        snapshot[ticker] = source?.[ticker] ?? null;
      }
      return snapshot;
    },
    perIndexStrategiesEqual(a, b) {
      for (const ticker of INDEX_TICKERS) {
        if ((a?.[ticker] ?? null) !== (b?.[ticker] ?? null)) return false;
      }
      return true;
    },
  },
};
</script>

<style scoped>
/* VOLATILE shares the red base of BEARISH; the diagonal stripe overlay is the
   visual cue that distinguishes "dangerous / changing" from "directional down". */
.badge.market-outlook-volatile {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(0, 0, 0, 0.18) 0,
    rgba(0, 0, 0, 0.18) 4px,
    transparent 4px,
    transparent 8px
  );
}
</style>
